import { useState } from 'react';
import { useView } from '@/hooks/useView';
import { useRows } from '@/hooks/useLive';
import { date as fmtDate, humanise, num, tonnes as fmtTonnes } from '@/lib/format';
import { useAuthStore } from '@/stores/authStore';
import { roleLock } from '@/lib/editability';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/ui/StatTile';
import { StatusChip } from '@/components/ui/StatusChip';
import { asRag } from '@/components/ui/statusTone';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { BlastDrawer } from './blasts/BlastDrawer';
import type { Material } from '@/types/models';

export interface BlastStock {
  blast_id: string;
  blast_no: string;
  blast_date: string | null;
  status: string;
  bench: string | null;
  area: string | null;
  volume_m3: number | null;
  density_t_m3: number | null;
  tonnes_estimated: number | null;
  tonnes_actual: number | null;
  waste_tonnes: number;
  tonnes_usable: number;
  tonnes_consumed: number;
  tonnes_adjusted: number;
  tonnes_remaining: number;
  consumed_pct: number | null;
  first_production_date: string | null;
  last_production_date: string | null;
}

interface Forecast {
  tonnes_remaining: number | null;
  active_blasts: number;
  window_days: number;
  production_days: number;
  avg_tonnes_per_production_day: number | null;
  avg_tonnes_per_calendar_day: number | null;
  production_days_remaining: number | null;
  amber_threshold_days: number;
  red_threshold_days: number;
  expected_depletion_date: string | null;
  status_colour: string | null;
}

const BANNER: Record<string, string> = {
  green: 'Blasted stock is sufficient. No blast needs to be scheduled yet.',
  amber: 'Plan the next blast now — stock is inside the warning window.',
  red: 'Blast required. Stock will run out before a normal drill-and-blast cycle completes.',
  grey: 'Not enough production history yet to forecast depletion.',
};

export function Blasts() {
  const { rows: stock, stale } = useView<BlastStock>('v_blast_stock');
  const { rows: forecastRows } = useView<Forecast>('v_blast_forecast');
  const materials = useRows<Material>('materials', 'code');
  const canEdit = useAuthStore((s) => s.hasRole('supervisor', 'manager', 'admin'));
  const role = useAuthStore((s) => s.profile?.role);
  // Everyone may open a record; who may change it is said inside (0046).
  const lock = roleLock(role, ['supervisor', 'manager', 'admin'], 'blasts');

  const [editing, setEditing] = useState<BlastStock | Record<string, never> | null>(null);
  const forecast = forecastRows?.[0];
  const rag = asRag(forecast?.status_colour);

  const columns: Column<BlastStock>[] = [
    { key: 'blast_no', label: 'Blast', render: (b) => <strong>{b.blast_no}</strong> },
    { key: 'blast_date', label: 'Date', value: (b) => b.blast_date ?? '', render: (b) => fmtDate(b.blast_date) },
    { key: 'bench', label: 'Bench' },
    {
      key: 'status', label: 'Status',
      render: (b) => (
        <StatusChip tone={b.status === 'in_production' ? 'green' : b.status === 'depleted' ? 'grey' : 'blue'}>
          {humanise(b.status)}
        </StatusChip>
      ),
    },
    {
      key: 'volume_m3', label: 'Volume', align: 'right',
      render: (b) => <span className="tabular">{num(b.volume_m3, 0)} m³</span>,
    },
    {
      key: 'density_t_m3', label: 'Density', align: 'right',
      render: (b) => <span className="tabular">{num(b.density_t_m3, 2)}</span>,
    },
    {
      key: 'tonnes_usable', label: 'Usable', align: 'right',
      render: (b) => <span className="tabular">{fmtTonnes(b.tonnes_usable)} t</span>,
    },
    {
      key: 'progress', label: 'Consumed', width: '180px',
      value: (b) => b.consumed_pct ?? 0,
      render: (b) => {
        const pctUsed = Math.min(100, Math.max(0, b.consumed_pct ?? 0));
        return (
          <div>
            <div className="stock-bar">
              <div className="stock-bar__used" style={{ width: `${pctUsed}%` }} />
              <div
                className="stock-bar__left"
                style={{ width: `${100 - pctUsed}%`, background: 'var(--green)' }}
              />
            </div>
            <div className="small muted tabular" style={{ marginTop: 4 }}>
              {fmtTonnes(b.tonnes_consumed)} used · {fmtTonnes(b.tonnes_remaining)} left
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      <Panel
        title="Blast stock forecast"
        actions={stale ? <StatusChip tone="amber">Cached</StatusChip> : undefined}
      >
        <div className={`forecast__banner forecast__banner--${rag}`}>
          <strong style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {rag === 'red' ? 'Blast now' : rag === 'amber' ? 'Plan blast' : rag === 'green' ? 'Sufficient' : 'No forecast'}
          </strong>
          <span>{BANNER[rag]}</span>
        </div>

        <div className="forecast">
          <StatTile label="Blasted stock" value={forecast?.tonnes_remaining} unit="t" large rag={rag} />
          <StatTile label="Average consumption" value={forecast?.avg_tonnes_per_production_day} unit="t/day"
            sub={`over ${forecast?.window_days ?? 30} days`} />
          <StatTile label="Production days left" value={forecast?.production_days_remaining} decimals={1}
            rag={rag} sub={`amber below ${forecast?.amber_threshold_days ?? 14}`} />
          <StatTile label="Expected depletion" value={fmtDate(forecast?.expected_depletion_date)} />
          <StatTile label="Active blasts" value={forecast?.active_blasts} />
          <StatTile label="Production days in window" value={forecast?.production_days}
            sub={`of ${forecast?.window_days ?? 30} calendar days`} />
        </div>

        <p className="small muted" style={{ marginBottom: 0 }}>
          Days remaining are <strong>production</strong> days, not calendar days —
          a quarry working 22 days a month must not be told its stock lasts 20
          calendar days. The depletion date converts back using the production
          days actually worked in the window.
        </p>
      </Panel>

      <Panel
        title="Blast register"
        flush
        actions={
          canEdit
            ? <Button style={{ minHeight: 32 }} onClick={() => setEditing({})}>+ New blast</Button>
            : <span className="small muted">Read only for your role</span>
        }
      >
        <div style={{ padding: 'var(--sp-3) var(--sp-3) 0' }} className="small muted">
          Consumption is derived from the daily diary — hauling activities that draw
          down blast stock. It is never captured twice.
        </div>
        <DataTable
          rows={stock}
          columns={columns}
          rowKey={(b) => b.blast_id}
          onRowClick={(b) => setEditing(b)}
          defaultSort="blast_date"
          searchPlaceholder="Search blasts…"
          empty="No blasts recorded yet."
        />
      </Panel>

      {editing && (
        <BlastDrawer
          blast={editing as BlastStock}
          materials={materials ?? []}
          locked={lock}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
